import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from "react";
import { LetterGuide } from "./LetterGuide";
import { LetterAnimator } from "./LetterAnimator";

// TracingCanvas — the interactive handwriting surface.
// Uses SVG + Pointer Events (mouse / touch / stylus). Records strokes as series
// of {x, y, t, pressure, strokeNumber}. Handles undo / clear via imperative ref.
export const TracingCanvas = forwardRef(
  (
    {
      letter,
      guideOpacity = 0.35,
      showStart = true,
      showLetterGuide = true,
      drawingEnabled = true,
      animatorRef,
      onStrokesChange,
    },
    ref
  ) => {
    const svgRef = useRef(null);
    const [strokes, setStrokes] = useState([]);
    const strokesRef = useRef([]);
    const [current, setCurrent] = useState(null);
    const currentRef = useRef(null);
    const drawing = useRef(false);
    const activePointer = useRef(null);

    const setStrokesSynced = useCallback(
      (next) => {
        strokesRef.current = next;
        setStrokes(next);
        onStrokesChange && onStrokesChange(next);
      },
      [onStrokesChange]
    );

    const updateCurrent = useCallback((val) => {
      currentRef.current = val;
      setCurrent(val);
    }, []);

    const toPoint = useCallback((e) => {
      const svg = svgRef.current;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const local = pt.matrixTransform(ctm.inverse());
      return {
        x: local.x,
        y: local.y,
        t: Date.now(),
        pressure: e.pressure || 0,
      };
    }, []);

    const onPointerDown = useCallback(
      (e) => {
        if (!drawingEnabled) return;
        // Only the primary pointer draws — ignore multi-touch gestures.
        if (!e.isPrimary) return;
        if (e.pointerType === "mouse" && e.button !== 0) return;
        e.preventDefault();
        drawing.current = true;
        activePointer.current = e.pointerId;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch (_) {}
        const strokeNumber = strokesRef.current.length + 1;
        updateCurrent({
          strokeNumber,
          points: [{ ...toPoint(e), strokeNumber }],
        });
      },
      [toPoint, drawingEnabled, updateCurrent]
    );

    const onPointerMove = useCallback(
      (e) => {
        if (!drawingEnabled) return;
        if (!drawing.current || e.pointerId !== activePointer.current) return;
        e.preventDefault();
        const p = toPoint(e);
        const c = currentRef.current;
        if (!c) return;
        updateCurrent({ ...c, points: [...c.points, p] });
      },
      [toPoint, drawingEnabled, updateCurrent]
    );

    const endStroke = useCallback(
      (e) => {
        if (!drawing.current || e.pointerId !== activePointer.current) return;
        drawing.current = false;
        activePointer.current = null;
        const c = currentRef.current;
        updateCurrent(null);
        if (c && c.points.length > 1) {
          setStrokesSynced([...strokesRef.current, c]);
        }
      },
      [updateCurrent, setStrokesSynced]
    );

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          updateCurrent(null);
          setStrokesSynced([]);
        },
        undo: () => setStrokesSynced(strokesRef.current.slice(0, -1)),
        getStrokes: () => strokesRef.current,
      }),
      [updateCurrent, setStrokesSynced]
    );

    const toPathD = (pts) =>
      pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

    const vb = (letter.viewBox || "0 0 100 100").split(/\s+/).map(Number);
    const aspect = `${vb[2] || 100} / ${vb[3] || 100}`;

    return (
      <div className="w-full select-none">
        <svg
          ref={svgRef}
          data-testid="tracing-canvas"
          viewBox={letter.viewBox}
          className="w-full h-auto rounded-3xl bg-white mx-auto"
          style={{ touchAction: "none", aspectRatio: aspect, maxHeight: "58vh", cursor: drawingEnabled ? "crosshair" : "default" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
        >
          <LetterGuide
            letter={letter}
            guideOpacity={guideOpacity}
            showStart={showStart}
            showLetter={showLetterGuide}
          />
          {animatorRef !== undefined && (
            <LetterAnimator ref={animatorRef} letter={letter} />
          )}

          {strokes.map((s, i) => (
            <path
              key={i}
              d={toPathD(s.points)}
              fill="none"
              stroke="#1F2937"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {current && current.points.length > 0 && (
            <path
              d={toPathD(current.points)}
              fill="none"
              stroke="#1F2937"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </div>
    );
  }
);

TracingCanvas.displayName = "TracingCanvas";
